// File: inquirypage.js
//
// The Inquiry page. A multi-step form that lets a user submit a
// request to the H.O.P.E team. The user picks a topic ("inquiry type"),
// picks an urgency level, acknowledges the disclaimer, and submits.
// The submission is saved to Firestore (in the `inquiries` collection),
// and depending on the inquiry type, may automatically open a
// message thread that an admin can respond in.
//
// Sections in this file:
//   - URGENCY               (the urgency levels + their colors)
//   - Helper components     (Step number circles, urgency badges,
//                            summary boxes used in the review step)
//   - Helper functions      (sorting types, creating threads, etc.)
//   - InquiryPage           (the page itself: 3-step wizard)
//
// What's likely to change:
//   - To add or rename urgency levels: edit the URGENCY array below.
//   - The list of inquiry topics is NOT in this file — it's stored in
//     Firestore and seeded from src/inquiryTypeCatalog.js. Admins can
//     edit the live list from the Admin portal.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import logo from "./assets/512.png";

import { auth } from "./firebaseConfig";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";

import { DEFAULT_INQUIRY_TYPES } from "./inquiryTypeCatalog";
import { COLORS, THEME, pageBackground } from "./theme";

const db = getFirestore();

// Bumping this string forces every user to re-read and re-acknowledge
// the disclaimer next time they open the inquiry page. Update it when
// the disclaimer text materially changes.
const DISCLAIMER_VERSION = "2026-04-23_v2";

// The urgency levels the user can pick from in step 2. The empty
// first entry is the "Select urgency..." placeholder. Edit labels,
// helper text, or colors here to change how each level appears.
const URGENCY = [
  {
    value: "",
    label: "Select urgency...",
    helper: "",
    bg: "#ffffff",
    color: "rgba(31,56,0,0.65)",
    border: THEME.border,
  },
  {
    value: "low",
    label: "Low",
    helper: "least urgent issue",
    bg: COLORS.greenLight,
    color: COLORS.greenDeep,
    border: "rgba(64,115,0,0.22)",
  },
  {
    value: "medium",
    label: "Medium",
    helper: "second most urgent issue",
    bg: COLORS.aquaLight,
    color: COLORS.aquaDeep,
    border: "rgba(0,96,110,0.22)",
  },
  {
    value: "high",
    label: "High",
    helper: "most urgent issue",
    bg: COLORS.yellowLight,
    color: COLORS.brown,
    border: "rgba(153,128,0,0.24)",
  },
];

const DISCLAIMER_TEXT = `
When reaching out to the Increasing H.O.P.E team, you will be asked to select the urgency of your situation. While the team works to plan a meeting quickly, Increasing H.O.P.E does not provide emergency services. Support is focused on longer-term advising and assistance rather than immediate crisis response.

For urgent needs, such as emergency rent assistance, the resource directory includes programs that may be able to help. The Increasing H.O.P.E team itself is not able to respond to emergencies.
`;

function IconInquiry({ className = "w-6 h-6" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 10h8M8 14h5" />
      <path d="M6 3h12a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z" />
    </svg>
  );
}

function StepDot({ index, current, label }) {
  const isActive = index === current;
  const isDone = index < current;

  return (
    <div className="flex items-center gap-3">
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-extrabold border"
        style={{
          backgroundColor: isActive
            ? THEME.text
            : isDone
            ? COLORS.aquaLight
            : "#ffffff",
          borderColor: isActive
            ? "transparent"
            : isDone
            ? "rgba(64,115,0,0.22)"
            : THEME.border,
          color: isActive ? "#ffffff" : isDone ? THEME.text : "rgba(31,56,0,0.4)",
        }}
      >
        {index + 1}
      </div>

      <div
        className="text-sm"
        style={{
          color: isActive ? THEME.text : "rgba(31,56,0,0.58)",
          fontWeight: isActive ? 800 : 600,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function UrgencyBadge({ urgency }) {
  const meta = URGENCY.find((u) => u.value === urgency) || URGENCY[0];

  if (!urgency) return <span>—</span>;

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-extrabold"
      style={{
        backgroundColor: meta.bg,
        borderColor: meta.border,
        color: meta.color,
      }}
    >
      {meta.label}
    </span>
  );
}

function SummaryBox({ label, children }) {
  return (
    <div
      className="rounded-2xl border p-4"
      style={{
        backgroundColor: "rgba(255,255,255,0.74)",
        borderColor: THEME.border,
      }}
    >
      <div
        className="text-xs font-semibold"
        style={{ color: "rgba(31,56,0,0.52)" }}
      >
        {label}
      </div>

      <div
        className="text-sm font-extrabold mt-1"
        style={{ color: THEME.text }}
      >
        {children || "—"}
      </div>
    </div>
  );
}

// Sorts inquiry types the way the dropdown shows them: by their
// `sortOrder` number first, then alphabetically by label.
function buildSortedTypes(list) {
  return [...list].sort((a, b) => {
    const orderA = Number.isFinite(Number(a.sortOrder))
      ? Number(a.sortOrder)
      : 9999;
    const orderB = Number.isFinite(Number(b.sortOrder))
      ? Number(b.sortOrder)
      : 9999;

    if (orderA !== orderB) return orderA - orderB;

    return String(a.label || "").localeCompare(String(b.label || ""));
  });
}

// First time someone opens this page on a fresh database, there are
// no inquiry types saved yet. This seeds the defaults from
// src/inquiryTypeCatalog.js so the dropdown isn't empty.
async function ensureInquiryTypesExist() {
  const snap = await getDocs(collection(db, "inquiryTypes"));
  if (!snap.empty) return;

  for (const item of DEFAULT_INQUIRY_TYPES) {
    await setDoc(
      doc(db, "inquiryTypes", item.key),
      {
        ...item,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      },
      { merge: true }
    );
  }
}

// Some inquiry types are configured (in the Admin portal) to
// automatically open a message thread when someone submits one.
// This function checks the selected type, and if it has
// `autoOpenThread = true`, it creates the thread. The opening
// "system" message is then posted server-side by the
// postAutoOpeningMessage Cloud Function (because Firestore rules
// only let staff post "system" messages, and the submitter usually
// isn't staff). Returns the new thread's id, or null if no thread
// should be created.
async function maybeCreateAutoThread({
  inquiryRef,
  inquiryId,
  inquiryPayload,
  selectedType,
  user,
}) {
  if (!selectedType?.id) return null;

  let latestType = selectedType;

  try {
    const freshSnap = await getDoc(doc(db, "inquiryTypes", selectedType.id));

    if (freshSnap.exists()) {
      latestType = { id: freshSnap.id, ...(freshSnap.data() || {}) };
    }
  } catch (err) {
    console.error("Failed to reload inquiry type before thread creation:", err);
  }

  if (latestType.autoOpenThread !== true) return null;

  const defaultMessage = (latestType.defaultThreadMessage || "").trim();
  if (!defaultMessage) return null;

  const participantUid = user?.uid || null;
  const participants = participantUid ? [participantUid] : [];

  const threadTitle = `Support: ${
    latestType.label || inquiryPayload.inquiryTypeLabel || "Inquiry"
  }`;

  // The opening "system" message is NOT posted from the client.
  // Firestore rules only let staff post senderUid: "system", and most
  // people submitting an inquiry aren't staff. A Cloud Function
  // (postAutoOpeningMessage) watches for new threads with
  // autoCreated: true and posts the opening message server-side.
  const threadRef = await addDoc(collection(db, "threads"), {
    title: threadTitle,
    inquiryId,
    inquiryTypeId: latestType.id,
    inquiryTypeKey: latestType.key || "",
    inquiryTypeLabel: latestType.label || "",
    createdForUid: participantUid,
    participants,
    status: "open",
    autoCreated: true,
    autoCreatedFromInquiryType: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessage: defaultMessage,
    lastSenderUid: "system",
  });

  await updateDoc(inquiryRef, {
    threadId: threadRef.id,
    threadStatus: "open",
    threadAutoCreated: true,
    threadCreatedAt: serverTimestamp(),
  });

  return threadRef.id;
}

export default function InquiryPage() {
  const navigate = useNavigate();

  // Which step of the wizard is showing (0, 1, or 2).
  const [step, setStep] = useState(0);

  // The list of inquiry topics loaded from Firestore + status flags.
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [bootstrappingTypes, setBootstrappingTypes] = useState(false);

  // The user's selections.
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [urgency, setUrgency] = useState("");

  // Submit + completion status.
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // The disclaimer must be scrolled to the bottom before the user can
  // continue past it. `disclaimerUnlocked` flips to true once they do.
  const disclaimerRef = useRef(null);
  const [disclaimerUnlocked, setDisclaimerUnlocked] = useState(false);

  const typeMenuRef = useRef(null);
  const typeButtonRef = useRef(null);
  const typeDropdownRef = useRef(null);

  const [typeOpen, setTypeOpen] = useState(false);
  const [typeDropdownStyle, setTypeDropdownStyle] = useState({
    left: "0px",
    top: "0px",
    bottom: "auto",
    width: "0px",
    maxHeight: "320px",
  });

  const stepTitles = ["Inquiry Type", "Urgency", "Disclaimer"];

  const updateTypeDropdownPosition = useCallback(() => {
    const button = typeButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const gap = 8;
    const viewportPadding = 12;
    const preferredMaxHeight = 360;

    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding - gap;
    const spaceAbove = rect.top - viewportPadding - gap;
    const openAbove = spaceBelow < 220 && spaceAbove > spaceBelow;

    const availableSpace = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(120, Math.min(preferredMaxHeight, availableSpace));

    const dropdownWidth = Math.min(
      rect.width,
      window.innerWidth - viewportPadding * 2
    );

    const dropdownLeft = Math.max(
      viewportPadding,
      Math.min(rect.left, window.innerWidth - dropdownWidth - viewportPadding)
    );

    setTypeDropdownStyle({
      left: `${dropdownLeft}px`,
      top: openAbove ? "auto" : `${rect.bottom + gap}px`,
      bottom: openAbove ? `${window.innerHeight - rect.top + gap}px` : "auto",
      width: `${dropdownWidth}px`,
      maxHeight: `${maxHeight}px`,
    });
  }, []);

  const toggleTypeMenu = () => {
    if (typeOpen) {
      setTypeOpen(false);
      return;
    }

    updateTypeDropdownPosition();
    setTypeOpen(true);
  };

  useEffect(() => {
    let cancelled = false;

    async function bootstrapIfNeeded() {
      try {
        setBootstrappingTypes(true);
        await ensureInquiryTypesExist();
      } catch (err) {
        console.error("Failed to ensure inquiry types exist:", err);
      } finally {
        if (!cancelled) setBootstrappingTypes(false);
      }
    }

    bootstrapIfNeeded();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const q = query(collection(db, "inquiryTypes"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((item) => item.active !== false && item.archived !== true);

        setInquiryTypes(buildSortedTypes(list));
        setLoadingTypes(false);
      },
      (err) => {
        console.error("Failed to load inquiry types:", err);
        setInquiryTypes([]);
        setLoadingTypes(false);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    if (selectedTypeId && !inquiryTypes.some((t) => t.id === selectedTypeId)) {
      setSelectedTypeId("");
    }
  }, [inquiryTypes, selectedTypeId]);

  useEffect(() => {
    const onDown = (e) => {
      if (!typeOpen) return;

      const clickedButtonArea = typeMenuRef.current?.contains(e.target);
      const clickedDropdownArea = typeDropdownRef.current?.contains(e.target);

      if (!clickedButtonArea && !clickedDropdownArea) {
        setTypeOpen(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [typeOpen]);

  useEffect(() => {
    if (!typeOpen) return;

    updateTypeDropdownPosition();

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onUpdate = () => updateTypeDropdownPosition();

    window.addEventListener("resize", onUpdate);
    window.addEventListener("scroll", onUpdate, true);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("resize", onUpdate);
      window.removeEventListener("scroll", onUpdate, true);
    };
  }, [typeOpen, updateTypeDropdownPosition]);

  const selectedType = useMemo(
    () => inquiryTypes.find((t) => t.id === selectedTypeId) || null,
    [inquiryTypes, selectedTypeId]
  );

  const canNext = useMemo(() => {
    if (step === 0) return Boolean(selectedTypeId);
    if (step === 1) return Boolean(urgency);
    return true;
  }, [step, selectedTypeId, urgency]);

  useEffect(() => {
    if (step !== 2) return;

    setDisclaimerUnlocked(false);

    const el = disclaimerRef.current;
    if (!el) return;

    const check = () => {
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      if (nearBottom) setDisclaimerUnlocked(true);
    };

    el.scrollTop = 0;

    const t = setTimeout(check, 50);
    return () => clearTimeout(t);
  }, [step]);

  const handleDisclaimerScroll = () => {
    const el = disclaimerRef.current;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
    if (nearBottom) setDisclaimerUnlocked(true);
  };

  const resetForNewInquiry = () => {
    setSubmitted(false);
    setSubmitting(false);
    setStep(0);
    setSelectedTypeId("");
    setUrgency("");
    setDisclaimerUnlocked(false);
    setTypeOpen(false);

    if (disclaimerRef.current) disclaimerRef.current.scrollTop = 0;
  };

  // Runs when the user clicks "Submit" on the final step. Writes the
  // inquiry to Firestore and (if configured) auto-opens a message
  // thread. Guards at the top stop double-submits and prevent
  // submitting an incomplete form.
  const handleSubmit = async () => {
    if (submitting) return;
    if (!disclaimerUnlocked) return;
    if (!selectedType) return;
    if (!urgency) return;

    setSubmitting(true);

    try {
      const user = auth.currentUser;

      const payload = {
        createdAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),

        createdByUid: user?.uid || null,
        createdByName: user?.displayName || null,
        createdByEmail: user?.email || null,

        inquiryTypeId: selectedType.id,
        inquiryTypeKey: selectedType.key || "",
        inquiryTypeLabel: selectedType.label || "",

        urgency,
        details: "",

        disclaimerVersion: DISCLAIMER_VERSION,
        disclaimerScrolledToEnd: true,

        status: "new",
        statusUpdatedAt: serverTimestamp(),

        claimedByUid: null,
        claimedByName: null,
        claimedByEmail: null,
        assignedToUid: null,
        assignedToName: null,

        tags: [],
        source: "web_intake",
      };

      const inquiryRef = await addDoc(collection(db, "inquiries"), payload);

      const threadId = await maybeCreateAutoThread({
        inquiryRef,
        inquiryId: inquiryRef.id,
        inquiryPayload: payload,
        selectedType,
        user,
      });

      if (threadId) {
        navigate("/messages", {
          state: {
            threadId,
          },
        });
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit inquiry:", err);
      const code = err?.code || err?.name || "unknown";
      const message = err?.message || String(err);
      alert(
        "Something went wrong submitting your inquiry. Please try again.\n\n" +
          "If this keeps happening, please share these details with support:\n" +
          "Code: " + code + "\n" +
          "Message: " + message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));
  const handleNext = () => setStep((s) => Math.min(2, s + 1));

  const inquiryTypeDropdown =
    typeOpen &&
    !loadingTypes &&
    !bootstrappingTypes &&
    inquiryTypes.length > 0 &&
    typeof document !== "undefined"
      ? createPortal(
          <div
            ref={typeDropdownRef}
            className="fixed rounded-2xl border bg-white shadow-lg overflow-hidden"
            style={{
              left: typeDropdownStyle.left,
              top: typeDropdownStyle.top,
              bottom: typeDropdownStyle.bottom,
              width: typeDropdownStyle.width,
              borderColor: THEME.border,
              zIndex: 9999,
            }}
          >
            <div
              className="overflow-y-auto overscroll-contain"
              style={{
                maxHeight: typeDropdownStyle.maxHeight,
              }}
            >
              {inquiryTypes.map((item) => {
                const selected = selectedTypeId === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setSelectedTypeId(item.id);
                      setTypeOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#E7F7D2] transition border-b last:border-b-0"
                    style={{
                      backgroundColor: selected ? COLORS.greenLight : "#ffffff",
                      borderColor: THEME.border,
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div
                          className="font-extrabold"
                          style={{ color: THEME.text }}
                        >
                          {item.label}
                        </div>

                        {item.description ? (
                          <div
                            className="mt-1 text-sm"
                            style={{ color: "rgba(31,56,0,0.62)" }}
                          >
                            {item.description}
                          </div>
                        ) : null}
                      </div>

                      {selected ? (
                        <div
                          className="text-xs font-extrabold shrink-0"
                          style={{ color: COLORS.aquaDark }}
                        >
                          Selected
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className="flex-1 relative" style={pageBackground}>
      {inquiryTypeDropdown}

      {/* --- Page header: logo on left, Home + Back on right --- */}
      <header
        className="sticky top-0 z-20 backdrop-blur border-b shadow-[0_6px_18px_rgba(31,56,0,0.16)]"
        style={{
          backgroundColor: THEME.headerBg,
          borderColor: "rgba(231,247,210,0.28)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/home")}
            className="flex items-center gap-3 rounded-xl px-2 py-1 hover:bg-white/10 transition"
            aria-label="Go to home"
            type="button"
          >
            <img src={logo} alt="Logo" className="w-13 h-12 object-contain" />

            <div className="leading-tight text-left">
              <div className="text-sm font-extrabold text-white">
                H.O.P.E Navigator
              </div>
              <div
                className="text-[12px] font-semibold"
                style={{ color: COLORS.yellowLight }}
              >
                Inquiry
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/home")}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
              type="button"
            >
              Home
            </button>

            <button
              onClick={() => navigate(-1)}
              className="text-sm font-extrabold text-white/90 hover:text-white transition rounded-xl px-3 py-2 hover:bg-white/10"
              type="button"
            >
              Back
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
        {/* --- "Submitted" success card. Shown after the user
                finishes the wizard. Gives them options to start a
                new inquiry or go back home. --- */}
        {submitted ? (
          <section
            className="max-w-2xl mx-auto backdrop-blur border rounded-3xl p-5 sm:p-6 lg:p-8"
            style={{
              backgroundColor: THEME.cardBg,
              borderColor: THEME.border,
              boxShadow: THEME.shadow,
            }}
          >
            <div
              className="inline-flex items-center rounded-full px-3 py-1 text-sm font-extrabold"
              style={{
                backgroundColor: COLORS.greenLight,
                color: THEME.text,
              }}
            >
              Submitted
            </div>

            <h1
              className="text-3xl sm:text-4xl font-extrabold mt-4"
              style={{ color: THEME.text }}
            >
              Your inquiry was submitted.
            </h1>

            <p className="mt-2" style={{ color: "rgba(31,56,0,0.66)" }}>
              What would you like to do next?
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <button
                onClick={resetForNewInquiry}
                className="w-full py-3 rounded-2xl text-white font-extrabold transition hover:opacity-90"
                style={{
                  backgroundColor: THEME.primary,
                  boxShadow: "0 10px 20px rgba(64,115,0,0.18)",
                }}
                type="button"
              >
                Open new inquiry
              </button>

              <button
                onClick={() => navigate("/home")}
                className="w-full py-3 rounded-2xl bg-white border font-extrabold hover:bg-[#E7F7D2] transition"
                style={{
                  color: THEME.text,
                  borderColor: THEME.border,
                }}
                type="button"
              >
                Return home
              </button>
            </div>
          </section>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
            <section
              className="backdrop-blur border rounded-3xl p-5 sm:p-6 lg:p-8"
              style={{
                backgroundColor: THEME.cardBg,
                borderColor: THEME.border,
                boxShadow: THEME.shadow,
              }}
            >
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-extrabold">
                <IconInquiry className="w-4 h-4" />
                Submit an Inquiry
              </div>

              <h1
                className="text-3xl sm:text-4xl font-extrabold mt-4"
                style={{ color: THEME.text }}
              >
                {stepTitles[step]}
              </h1>

              <p
                className="mt-2 max-w-2xl"
                style={{ color: "rgba(31,56,0,0.66)" }}
              >
                Complete each step so the Increasing H.O.P.E team knows how to
                route your request.
              </p>

              <div className="lg:hidden mt-6">
                <div className="flex items-center gap-2">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-2 flex-1 rounded-full"
                      style={{
                        backgroundColor:
                          i <= step ? THEME.text : "rgba(31,56,0,0.14)",
                      }}
                    />
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {stepTitles.map((title, idx) => (
                    <span
                      key={title}
                      className="text-xs px-3 py-1 rounded-full border"
                      style={{
                        backgroundColor:
                          idx === step ? COLORS.yellowLight : "#ffffff",
                        borderColor:
                          idx === step
                            ? "rgba(153,128,0,0.24)"
                            : THEME.border,
                        color:
                          idx === step ? COLORS.brown : "rgba(31,56,0,0.58)",
                        fontWeight: idx === step ? 800 : 600,
                      }}
                    >
                      {idx + 1}. {title}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                {step === 0 && (
                  <>
                    <div
                      className="text-lg font-extrabold"
                      style={{ color: THEME.text }}
                    >
                      Choose an inquiry type.
                    </div>

                    <div
                      className="text-sm mt-1"
                      style={{ color: "rgba(31,56,0,0.62)" }}
                    >
                      Choose the option that best matches what you need help
                      with.
                    </div>

                    {bootstrappingTypes ? (
                      <div
                        className="mt-4 rounded-2xl border p-4 text-sm"
                        style={{
                          backgroundColor: THEME.cardBgSoft,
                          borderColor: THEME.border,
                          color: "rgba(31,56,0,0.62)",
                        }}
                      >
                        Setting up inquiry types…
                      </div>
                    ) : null}

                    <div className="mt-4" ref={typeMenuRef}>
                      <label
                        className="block text-sm font-extrabold mb-2"
                        style={{ color: "rgba(31,56,0,0.72)" }}
                      >
                        Inquiry Type
                      </label>

                      <button
                        ref={typeButtonRef}
                        type="button"
                        onClick={toggleTypeMenu}
                        disabled={loadingTypes || bootstrappingTypes}
                        className="w-full py-3 px-4 rounded-2xl bg-white border text-left hover:bg-[#E7F7D2] transition focus:outline-none disabled:opacity-60"
                        style={{
                          borderColor: THEME.border,
                          boxShadow: "0 8px 20px rgba(31,56,0,0.06)",
                        }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div
                            className="font-semibold"
                            style={{ color: "rgba(31,56,0,0.78)" }}
                          >
                            {selectedType?.label ||
                              (loadingTypes || bootstrappingTypes
                                ? "Loading inquiry types..."
                                : "Select an inquiry type...")}
                          </div>

                          <div
                            className="text-sm"
                            style={{ color: "rgba(31,56,0,0.42)" }}
                          >
                            {typeOpen ? "▲" : "▼"}
                          </div>
                        </div>
                      </button>

                      {!loadingTypes &&
                      !bootstrappingTypes &&
                      inquiryTypes.length === 0 ? (
                        <div
                          className="mt-3 rounded-2xl border p-4 text-sm"
                          style={{
                            backgroundColor: THEME.cardBgSoft,
                            borderColor: THEME.border,
                            color: "rgba(31,56,0,0.62)",
                          }}
                        >
                          No active inquiry types found.
                        </div>
                      ) : null}
                    </div>

                    {selectedType?.description ? (
                      <div
                        className="mt-4 rounded-2xl border p-4"
                        style={{
                          backgroundColor: THEME.cardBgSoft,
                          borderColor: THEME.border,
                        }}
                      >
                        <div
                          className="text-xs font-extrabold uppercase tracking-wide"
                          style={{ color: "rgba(31,56,0,0.48)" }}
                        >
                          Description
                        </div>

                        <div
                          className="mt-2 text-sm"
                          style={{ color: "rgba(31,56,0,0.7)" }}
                        >
                          {selectedType.description}
                        </div>
                      </div>
                    ) : null}
                  </>
                )}

                {step === 1 && (
                  <>
                    <div
                      className="text-lg font-extrabold"
                      style={{ color: THEME.text }}
                    >
                      Select urgency.
                    </div>

                    <div
                      className="text-sm mt-1"
                      style={{ color: "rgba(31,56,0,0.62)" }}
                    >
                      This helps the team understand what to address first.
                    </div>

                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      {URGENCY.filter((u) => u.value).map((u) => {
                        const selected = urgency === u.value;

                        return (
                          <button
                            key={u.value}
                            onClick={() => setUrgency(u.value)}
                            className="w-full text-left rounded-2xl border px-4 py-4 transition hover:-translate-y-0.5"
                            style={{
                              backgroundColor: selected ? u.bg : "#ffffff",
                              borderColor: selected ? u.border : THEME.border,
                              boxShadow: selected
                                ? "0 14px 28px rgba(31,56,0,0.12)"
                                : "0 8px 20px rgba(31,56,0,0.06)",
                            }}
                            type="button"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div
                                className="text-base font-extrabold"
                                style={{ color: THEME.text }}
                              >
                                {u.label}
                              </div>

                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-extrabold"
                                style={{
                                  backgroundColor: u.bg,
                                  borderColor: u.border,
                                  color: u.color,
                                }}
                              >
                                {u.label}
                              </span>
                            </div>

                            <div
                              className="text-sm mt-1"
                              style={{ color: "rgba(31,56,0,0.58)" }}
                            >
                              {u.helper}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {step === 2 && (
                  <>
                    <div
                      className="text-lg font-extrabold"
                      style={{ color: THEME.text }}
                    >
                      Review the disclaimer.
                    </div>

                    <div className="mt-4 grid sm:grid-cols-2 gap-3 mb-4">
                      <SummaryBox label="Inquiry Type">
                        {selectedType?.label || "—"}
                      </SummaryBox>

                      <SummaryBox label="Urgency">
                        <UrgencyBadge urgency={urgency} />
                      </SummaryBox>
                    </div>

                    <div
                      className="rounded-2xl border overflow-hidden"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.86)",
                        borderColor: THEME.border,
                      }}
                    >
                      <div
                        className="px-4 py-3 border-b flex items-center justify-between gap-3"
                        style={{
                          backgroundColor: "rgba(255,255,255,0.72)",
                          borderColor: THEME.border,
                        }}
                      >
                        <div
                          className="text-sm font-extrabold"
                          style={{ color: THEME.text }}
                        >
                          Disclaimer
                        </div>
                      </div>

                      <div
                        ref={disclaimerRef}
                        onScroll={handleDisclaimerScroll}
                        className="h-56 overflow-y-auto px-4 py-3 text-sm whitespace-pre-line"
                        style={{ color: "rgba(31,56,0,0.72)" }}
                      >
                        {DISCLAIMER_TEXT}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-between">
                <button
                  onClick={handleBack}
                  disabled={step === 0}
                  className="py-3 px-5 rounded-2xl bg-white border font-extrabold hover:bg-[#E7F7D2] disabled:opacity-40 disabled:cursor-not-allowed transition"
                  style={{
                    color: THEME.text,
                    borderColor: THEME.border,
                  }}
                  type="button"
                >
                  Back
                </button>

                <div className="flex flex-col sm:flex-row gap-3">
                  {step === 2 ? (
                    <button
                      onClick={handleSubmit}
                      disabled={
                        !disclaimerUnlocked ||
                        submitting ||
                        !selectedType ||
                        bootstrappingTypes
                      }
                      className="py-3 px-5 rounded-2xl text-white font-extrabold disabled:cursor-not-allowed transition hover:opacity-90 disabled:hover:opacity-100"
                      style={{
                        backgroundColor:
                          !disclaimerUnlocked ||
                          submitting ||
                          !selectedType ||
                          bootstrappingTypes
                            ? "rgba(31,56,0,0.28)"
                            : THEME.primary,
                        boxShadow:
                          disclaimerUnlocked && !submitting
                            ? "0 10px 20px rgba(64,115,0,0.18)"
                            : "none",
                      }}
                      type="button"
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </button>
                  ) : (
                    <button
                      onClick={handleNext}
                      disabled={!canNext || bootstrappingTypes}
                      className="py-3 px-5 rounded-2xl text-white font-extrabold disabled:cursor-not-allowed transition hover:opacity-90 disabled:hover:opacity-100"
                      style={{
                        backgroundColor:
                          canNext && !bootstrappingTypes
                            ? THEME.primary
                            : "rgba(31,56,0,0.28)",
                        boxShadow:
                          canNext && !bootstrappingTypes
                            ? "0 10px 20px rgba(64,115,0,0.18)"
                            : "none",
                      }}
                      type="button"
                    >
                      Next
                    </button>
                  )}
                </div>
              </div>
            </section>

            <aside className="hidden lg:block">
              <div className="sticky top-[64px] sm:top-[72px] space-y-4">
                <div
                  className="rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur"
                  style={{
                    backgroundColor: THEME.cardBg,
                    borderColor: THEME.border,
                    boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
                  }}
                >
                  <div
                    className="text-sm font-extrabold"
                    style={{ color: THEME.text }}
                  >
                    Progress
                  </div>

                  <div className="mt-5 space-y-4">
                    {stepTitles.map((t, idx) => (
                      <StepDot key={t} index={idx} current={step} label={t} />
                    ))}
                  </div>
                </div>

                <div
                  className="rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur"
                  style={{
                    backgroundColor: THEME.cardBg,
                    borderColor: THEME.border,
                    boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
                  }}
                >
                  <div
                    className="text-sm font-extrabold"
                    style={{ color: THEME.text }}
                  >
                    Current Selection
                  </div>

                  <div className="mt-4 space-y-3">
                    <SummaryBox label="Inquiry Type">
                      {selectedType?.label || "—"}
                    </SummaryBox>

                    <SummaryBox label="Urgency">
                      <UrgencyBadge urgency={urgency} />
                    </SummaryBox>
                  </div>
                </div>

                {selectedType?.followUpQuestions?.length ? (
                  <div
                    className="rounded-2xl border p-4 sm:p-5 lg:p-6 backdrop-blur"
                    style={{
                      backgroundColor: THEME.cardBg,
                      borderColor: THEME.border,
                      boxShadow: "0 14px 30px rgba(31,56,0,0.1)",
                    }}
                  >
                    <div
                      className="text-sm font-extrabold"
                      style={{ color: THEME.text }}
                    >
                      Likely Follow-Up Topics
                    </div>

                    <ul
                      className="mt-3 space-y-2 text-sm"
                      style={{ color: "rgba(31,56,0,0.66)" }}
                    >
                      {selectedType.followUpQuestions
                        .slice(0, 5)
                        .map((item, idx) => (
                          <li key={`${selectedType.id}_${idx}`}>• {item}</li>
                        ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}